import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import TaskSkeleton from './TaskSkeleton';
import moment from 'moment';
import Select from 'react-select';

export default function SchoolAgentMaster() {
    const { http, user } = AuthUser();
    const [agents, setAgents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [selectedAgentUserId, setSelectedAgentUserId] = useState(null);
    const [notes, setNotes] = useState([]);
    const [noteForm, setNoteForm] = useState({
        note: '',
        school_agent_user_id: null,
        user_id: user.id,
        company_id: user.company_id,
    });
    const [editingNote, setEditingNote] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    // User options for react-select
    const userOptions = useMemo(() => users.map(u => ({
        value: u.id,
        label: `${u.name} ${u.last_name || ''}`.trim()
    })), [users]);

    // Custom styles for react-select
    const selectStyles = {
        control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
        valueContainer: (base) => ({ ...base, padding: '0 6px' }),
        input: (base) => ({ ...base, margin: '0', padding: '0' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    useEffect(() => {
        fetchAgents();
        fetchUsers();
    }, []);

    const fetchAgents = () => {
        setIsLoading(true);
        http.get(`/agent_masters?company_id=${user.company_id}`)
            .then((res) => {
                setIsLoading(false);
                if (res.data.success && Array.isArray(res.data.data)) {
                    setAgents(res.data.data);
                } else {
                    console.error("API response is not as expected:", res.data);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching agent masters:", error);
                toast.error("Failed to load agent masters");
            });
    };

    const fetchUsers = () => {
        http.get(`/users_by_company_department?company_id=${user.company_id}&department_id=4`)
            .then((res) => {
                if (res.data.success && Array.isArray(res.data.users)) {
                    setUsers(res.data.users);
                } else {
                    console.error("API response for users is not as expected:", res.data);
                }
            })
            .catch((error) => {
                console.error("Error fetching users:", error);
            });
    };

    const fetchNotes = (schoolAgentUserId) => {
        http.get(`/agent_note?company_id=${user.company_id}&school_agent_user_id=${schoolAgentUserId}`)
            .then((res) => {
                if (res.data.status === 'success' && Array.isArray(res.data.data)) {
                    setNotes(res.data.data);
                }
            })
            .catch((error) => {
                console.error("Error fetching notes:", error);
            });
    };

    const getUserName = (userId) => {
        const foundUser = users.find(u => u.id === userId);
        return foundUser ? `${foundUser.name} ${foundUser.last_name || ''}` : 'Unknown User';
    };

    const openNoteModal = (agentUserId) => {
        setSelectedAgentUserId(agentUserId);
        setNoteForm({
            note: '',
            school_agent_user_id: agentUserId,
            user_id: user.id,
            company_id: user.company_id,
        });
        setEditingNote(null);
        setShowNoteModal(true);
        fetchNotes(agentUserId);
    };

    const closeNoteModal = () => {
        setShowNoteModal(false);
        setSelectedAgentUserId(null);
        setEditingNote(null);
        setNotes([]);
        setNoteForm({
            note: '',
            school_agent_user_id: null,
            user_id: user.id,
            company_id: user.company_id,
        });
    };

    const handleNoteChange = (e) => {
        setNoteForm({
            ...noteForm,
            [e.target.name]: e.target.value,
        });
    };

    const handleNoteSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingNote) {
                const response = await http.put(`/agent_note_update/${editingNote.id}`, {
                    note: noteForm.note,
                });
                if (response.data.status === 'success') {
                    toast.success("Note updated successfully!");
                    fetchNotes(selectedAgentUserId);
                    setEditingNote(null);
                    setNoteForm({
                        ...noteForm,
                        note: '',
                    });
                }
            } else {
                const response = await http.post('/agent_note_add', noteForm);
                if (response.data.status === 'success') {
                    toast.success("Note added successfully!");
                    fetchNotes(selectedAgentUserId);
                    setNoteForm({
                        ...noteForm,
                        note: '',
                    });
                }
            }
        } catch (error) {
            console.error("Error saving note:", error);
            toast.error("Failed to save note");
        }
    };

    const handleEditNote = (note) => {
        setEditingNote(note);
        setNoteForm({
            ...noteForm,
            note: note.note,
        });
    };

    const handleDeleteNote = async (noteId) => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            try {
                const response = await http.delete(`/agent_note_delete/${noteId}`);
                if (response.data.status === 'success') {
                    toast.success("Note deleted successfully!");
                    fetchNotes(selectedAgentUserId);
                }
            } catch (error) {
                console.error("Error deleting note:", error);
                toast.error("Failed to delete note");
            }
        }
    };

    // Filter agents
    const filteredAgents = selectedUser 
        ? agents.filter(a => a.user_id === selectedUser.value)
        : agents;

    // Group agents by user for summary
    const getSummary = () => {
        const summary = {};
        agents.forEach(agent => {
            if (!summary[agent.user_id]) {
                summary[agent.user_id] = {
                    user_id: agent.user_id,
                    name: getUserName(agent.user_id),
                    totalAllocation: 0,
                    classes: 0,
                    status_counts: { Pending: 0, Approval: 0, 'Not Approval': 0, Canceled: 0 }
                };
            }
            summary[agent.user_id].totalAllocation += agent.allocation || 0;
            summary[agent.user_id].classes += 1;
            if (agent.status_counts) {
                summary[agent.user_id].status_counts.Pending += agent.status_counts.Pending || 0;
                summary[agent.user_id].status_counts.Approval += agent.status_counts.Approval || 0;
                summary[agent.user_id].status_counts['Not Approval'] += agent.status_counts['Not Approval'] || 0;
                summary[agent.user_id].status_counts.Canceled += agent.status_counts.Canceled || 0;
            }
        });
        return Object.values(summary);
    };

    const summaryData = getSummary();

    return (
        <>
            <Toaster />
            {showNoteModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header py-2 bg-primary text-white">
                                <h6 className="modal-title mb-0">
                                    <i className="fa fa-sticky-note me-2"></i>
                                    Notes - {selectedAgentUserId ? getUserName(selectedAgentUserId) : ''}
                                </h6>
                                <button type="button" className="btn-close btn-close-white" onClick={closeNoteModal}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleNoteSubmit}>
                                    <div className="form-group mb-2">
                                        <textarea
                                            className="form-control"
                                            name="note"
                                            rows="3"
                                            value={noteForm.note}
                                            onChange={handleNoteChange}
                                            required
                                            placeholder="Enter note here..."
                                        />
                                    </div>
                                    <div className="form-group mb-3">
                                        <button type="submit" className="btn btn-primary btn-sm">
                                            {editingNote ? 'Update' : 'Add Note'}
                                        </button>
                                        {editingNote && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm ms-2"
                                                onClick={() => {
                                                    setEditingNote(null);
                                                    setNoteForm({ ...noteForm, note: '' });
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </form>
                                <hr className="my-2" />
                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    {notes.length > 0 ? (
                                        notes.map((note) => (
                                            <div key={note.id} className="card mb-2">
                                                <div className="card-body py-2 px-3">
                                                    <p className="card-text mb-1">{note.note}</p>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <small className="text-muted">
                                                            {moment(note.created_at).format("DD MMM, YY HH:mm")}
                                                            {note.user && ` - ${note.user.name}`}
                                                        </small>
                                                        <div>
                                                            <button className="btn btn-sm btn-link py-0" onClick={() => handleEditNote(note)}>Edit</button>
                                                            <button className="btn btn-sm btn-link text-danger py-0" onClick={() => handleDeleteNote(note.id)}>Delete</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted text-center">No notes yet</p>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer py-2">
                                <button type="button" className="btn btn-secondary btn-sm" onClick={closeNoteModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="container-fluid px-3 pt-2">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">School Consult Masters</h5>
                    <Link to="/school_agent_add" className="btn btn-success btn-sm">
                        <i className="fa fa-plus me-1"></i>Add New
                    </Link>
                </div>

                {/* Filter */}
                <div className="card mb-2 border-0 shadow-sm" style={{ background: '#f8f9fa' }}>
                    <div className="card-body py-2 px-3">
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width: '200px' }}>
                                <Select
                                    options={userOptions}
                                    value={selectedUser}
                                    onChange={setSelectedUser}
                                    placeholder="Select Consultant..."
                                    isClearable
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            {selectedUser && (
                                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedUser(null)} title="Clear Filter">
                                    <i className="fa fa-times me-1"></i>Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <TaskSkeleton />
                ) : (
                    <>
                        {/* Summary Table */}
                        <div className="card mb-3">
                            <div className="card-header py-2 bg-info text-white">
                                <strong><i className="fa fa-chart-pie me-2"></i>Consultant Summary</strong>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Consultant</th>
                                                <th className="text-center">Classes</th>
                                                <th className="text-center">Total Allocation</th>
                                                <th className="text-center"><span className="badge bg-success">Approved</span></th>
                                                <th className="text-center"><span className="badge bg-warning text-dark">Not Approved</span></th>
                                                <th className="text-center"><span className="badge bg-info">Hold</span></th>
                                                <th className="text-center"><span className="badge bg-danger">Cancelled</span></th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summaryData.filter(s => !selectedUser || s.user_id === selectedUser.value).map((row, idx) => (
                                                <tr key={row.user_id}>
                                                    <td>{idx + 1}</td>
                                                    <td><strong>{row.name}</strong></td>
                                                    <td className="text-center">{row.classes}</td>
                                                    <td className="text-center"><span className="badge bg-primary">{row.totalAllocation}</span></td>
                                                    <td className="text-center"><strong className="text-success">{row.status_counts.Approval}</strong></td>
                                                    <td className="text-center"><strong className="text-warning">{row.status_counts['Not Approval']}</strong></td>
                                                    <td className="text-center"><strong className="text-info">{row.status_counts.Pending}</strong></td>
                                                    <td className="text-center"><strong className="text-danger">{row.status_counts.Canceled}</strong></td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary py-0 px-2"
                                                            onClick={() => openNoteModal(row.user_id)}
                                                            title="View/Add Notes"
                                                        >
                                                            <i className="fas fa-sticky-note"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {summaryData.length === 0 && (
                                                <tr><td colSpan="9" className="text-center text-muted">No data</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Class-wise Details Table */}
                        <div className="card">
                            <div className="card-header py-2 bg-secondary text-white">
                                <strong><i className="fa fa-list me-2"></i>Class-wise Allocation Details</strong>
                                <span className="badge bg-light text-dark ms-2">{filteredAgents.length} records</span>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-sm table-striped table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Consultant</th>
                                                <th>Class</th>
                                                <th className="text-center">Allocation</th>
                                                <th className="text-center">Class Capacity</th>
                                                <th className="text-center">Open Seats</th>
                                                <th className="text-center"><span className="badge bg-success">Appr</span></th>
                                                <th className="text-center"><span className="badge bg-warning text-dark">Not Appr</span></th>
                                                <th className="text-center"><span className="badge bg-info">Hold</span></th>
                                                <th className="text-center"><span className="badge bg-danger">Cancel</span></th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAgents.length > 0 ? (
                                                filteredAgents.map((agent, index) => (
                                                    <tr key={agent.id}>
                                                        <td>{index + 1}</td>
                                                        <td>{getUserName(agent.user_id)}</td>
                                                        <td><span className="badge bg-light text-dark">{agent.class || '-'}</span></td>
                                                        <td className="text-center">
                                                            <span className="badge bg-primary">{agent.allocation || 0}</span>
                                                        </td>
                                                        <td className="text-center">
                                                            {agent.class_allocation ? agent.class_allocation.capacity || '-' : '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            {agent.class_allocation ? (
                                                                <>
                                                                    {agent.class_allocation.open_seats || 0}
                                                                    {agent.class_allocation.open_seats === 0 && 
                                                                        <span className="badge bg-danger ms-1">Full</span>
                                                                    }
                                                                </>
                                                            ) : '-'}
                                                        </td>
                                                        <td className="text-center">
                                                            <strong className="text-success">{agent.status_counts?.Approval || 0}</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            <strong className="text-warning">{agent.status_counts?.['Not Approval'] || 0}</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            <strong className="text-info">{agent.status_counts?.Pending || 0}</strong>
                                                        </td>
                                                        <td className="text-center">
                                                            <strong className="text-danger">{agent.status_counts?.Canceled || 0}</strong>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {moment(agent.created_at).format('DD MMM YY')}
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="11" className="text-center text-muted">
                                                        No school agents found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
